import { useState, useEffect } from 'react'
import { sb, API_URL } from '../utils/config'
import { studioEvents } from '../utils/metaPixel'
import merlinImg from '../assets/merlin.png'

// Page d'atterrissage du magic link en flux `token_hash` (PAS le flux
// implicite par défaut). Pourquoi : le flux implicite finalise la connexion
// sur un simple GET de l'URL — donc les scanners de mail (Gmail, antivirus,
// passerelles sécu) qui PRÉCHARGENT le lien consomment le token à usage
// unique avant que l'user clique. Résultat mesuré : « Token has expired or
// is invalid » (13×), et le code à 6 chiffres — qui partage le même token —
// grillé lui aussi (0/19 complétions). Ici, `verifyOtp` ne part QUE sur un
// clic humain : un préchargement (aucun clic) ne consomme rien.
//
// Bonus cross-navigateur : le lien porte gs (guest_session_id = anon_id du
// scan) + h (handle @) DANS l'URL. En webview Instagram le lien s'ouvre dans
// Safari (localStorage cloisonné, vide) — on rattache donc le scan invité
// depuis ces paramètres d'URL, pas depuis le localStorage. L'analyse
// préchargée réapparaît dans le nouveau navigateur.

// Construit le redirectTo passé à signInWithOtp. Devient `{{ .RedirectTo }}`
// dans le template Supabase, qui y accole `&token_hash=…&type=email`. On y
// embarque gs (guest_session_id = anon_id du scan) + h (handle @) pour que le
// scan invité se rattache même dans un navigateur vierge (webview → Safari).
// `claim` conservé pour compat avec l'ancien effet global de StudioPage.
export function buildMagicRedirect() {
  let anonId = '', handle = ''
  try {
    anonId = localStorage.getItem('studio_anon_id') || ''
    handle = (localStorage.getItem('studio_prefill_handle') || '').trim()
  } catch { /* best effort */ }
  const p = new URLSearchParams({ studio: '1' })
  if (anonId) { p.set('gs', anonId); p.set('claim', anonId) }
  if (handle) p.set('h', handle)
  // Visiteur deal (?deal=1) : le drapeau vit dans le localStorage, qui ne
  // survit pas au saut de navigateur (webview → Safari). On le porte dans
  // l'URL du magic link, comme le scan.
  try { if (localStorage.getItem('studio_deal_offer') === '1') p.set('deal', '1') } catch { /* best effort */ }
  return `${window.location.origin}/?${p.toString()}`
}

export function readMagicConfirm() {
  try {
    const q = new URLSearchParams(window.location.search)
    const token_hash = q.get('token_hash') || ''
    const type = q.get('type') || 'email'
    if (!token_hash) return null
    return {
      token_hash,
      type,
      gs: (q.get('gs') || q.get('claim') || '').trim(),
      h: (q.get('h') || '').trim(),
      deal: q.get('deal') === '1',
    }
  } catch { return null }
}

export default function MagicLinkConfirm({ params }) {
  const [state, setState] = useState('idle') // idle | loading | error
  const [error, setError] = useState('')

  useEffect(() => {
    try { studioEvents.magicConfirmViewed({ has_scan: !!params.gs }) } catch {}
    // Réhydrate le drapeau deal dans CE navigateur dès l'atterrissage.
    try { if (params.deal) localStorage.setItem('studio_deal_offer', '1') } catch { /* best effort */ }
  }, [])

  async function confirm() {
    if (state === 'loading') return
    setState('loading'); setError('')
    try {
      // Le type du token dépend du contexte Supabase (nouvel inscrit → 'signup'
      // ou 'email', connexion → 'magiclink'/'email'). On tente celui de l'URL
      // puis les variantes, pour ne jamais échouer sur un simple mismatch de type.
      const types = [params.type, 'email', 'magiclink', 'signup'].filter((t, i, a) => t && a.indexOf(t) === i)
      let vErr = null, ok = false
      for (const t of types) {
        const { error: e } = await sb.auth.verifyOtp({ token_hash: params.token_hash, type: t })
        if (!e) { ok = true; break }
        vErr = e
        // Token déjà consommé/expiré : inutile d'essayer les autres types.
        if (/expired|already|consumed/i.test(e.message || '')) break
      }
      if (!ok) throw (vErr || new Error('verify failed'))
      // Rattachement du scan invité depuis les params d'URL (navigateur vierge
      // en webview → Safari : le localStorage ne suit pas, on s'appuie sur
      // gs/h portés par le lien).
      let claimed = false
      try {
        const { data: sess } = await sb.auth.getSession()
        const tok = sess?.session?.access_token
        if (tok && (params.gs || params.h)) {
          const res = await fetch(`${API_URL}/api/studio/claim-guest-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ guest_session_id: params.gs, ig_username: params.h, deal: !!params.deal }),
          })
          const d = await res.json().catch(() => null)
          claimed = !!(d && d.claimed)
        }
      } catch { /* le rattachement peut échouer sans bloquer la connexion */ }
      try {
        studioEvents.signupCompleted({ method: 'magic_link_confirm', mode: 'token_hash', claimed })
        studioEvents.guestSessionClaimed({ ok: claimed, via: 'token_hash_url' })
      } catch {}
      // URL propre + on rentre dans le Studio loggué : l'analyse préchargée
      // s'affiche (session invité désormais attachée au compte).
      try { window.history.replaceState(null, '', '/?studio=1') } catch {}
      window.location.replace('/?studio=1')
    } catch (err) {
      setState('error')
      const msg = err?.message || ''
      try { studioEvents.magicConfirmFailed({ error: msg.substring(0, 100) }) } catch {}
      setError(/expired|invalid|token/i.test(msg)
        ? "Ce lien a expiré. Retourne sur Merlin et redemande un lien de connexion."
        : (msg || "La connexion a échoué, réessaie."))
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-5">
      <div className="w-full max-w-sm text-center">
        <img src={merlinImg} alt="" className="w-14 h-14 object-contain mx-auto mb-4"
          style={{ filter: 'drop-shadow(0 4px 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.30))' }} />
        <h1 className="text-[20px] font-extrabold text-ink leading-snug">Un dernier clic pour te connecter</h1>
        <p className="text-[14px] text-ink-faint mt-2">
          {params.gs
            ? "On rouvre ton analyse juste ici."
            : "On te connecte à ton compte Merlin."}
        </p>

        {state === 'error' ? (
          <>
            <p className="text-[13px] text-red-600 mt-4">{error}</p>
            <button onClick={confirm}
              className="mt-4 w-full rounded-xl bg-ink text-white font-bold py-3.5 text-[15px] active:scale-[0.99] transition">
              Réessayer
            </button>
          </>
        ) : (
          <button onClick={confirm} disabled={state === 'loading'}
            className="mt-6 w-full rounded-xl font-bold py-3.5 text-[15px] text-white active:scale-[0.99] transition disabled:opacity-70"
            style={{ background: 'var(--merlin-accent, #fe8204)' }}>
            {state === 'loading' ? 'Connexion…' : 'Confirmer ma connexion'}
          </button>
        )}

        <p className="text-[11px] text-ink-faint mt-4 leading-relaxed">
          Ce clic protège ton compte : il empêche les scanners de mail de griller ton lien.
        </p>
      </div>
    </div>
  )
}
